<script lang="ts">
  import { autofocus } from '#app/lib/components/actions/autofocus'
  import { autosize } from '#app/lib/components/actions/autosize'
  import type { FlashState } from '#app/lib/components/flash.svelte'
  import { icon } from '#app/lib/icons'
  import { getActionKey } from '#app/lib/key_events'
  import { userContent } from '#app/lib/user_content'
  import { isSelectionClick } from '#app/lib/utils'
  import type { ClickEvent } from '#app/types/declarations/svelte'
  import { updateItems } from '#inventory/lib/item_actions'
  import type { SerializedItem } from '#inventory/lib/items'
  import { i18n, I18n } from '#user/lib/i18n'

  let {
    item = $bindable(),
    flash = $bindable(),
  }: {
    item: SerializedItem
    flash?: FlashState
  } = $props()

  const { restricted, visibilitySummaryIconName } = item
  let { details = '' } = $state(item)

  let editMode = $state(false)

  async function save () {
    editMode = false
    try {
      await updateItems({
        items: [ item ],
        attribute: 'details',
        value: details,
      })
      item.details = details
    } catch (err) {
      flash = err
    }
  }

  function onKeyDown (e) {
    const key = getActionKey(e)
    if (key === 'esc') editMode = false
    else if (e.ctrlKey && key === 'enter') save()
    e.stopPropagation()
  }

  function onEditButtonClick (e: ClickEvent) {
    if (isSelectionClick(e)) return
    editMode = true
  }
</script>

{#snippet header()}
  <div class="header">
    <span class="section-label">{I18n('details')}</span>
    {#if !restricted}
      <span class="indicator" title={i18n('this is visible by anyone who can see this item')}>
        {#if visibilitySummaryIconName}
          {@html icon(visibilitySummaryIconName)}
        {/if}
      </span>
      <span>
        {@html icon('pencil')}
      </span>
    {/if}
  </div>
{/snippet}

{#if editMode}
  <div class="item-details">
    {@render header()}
    <textarea
      placeholder={`(${i18n('details_placeholder')})`}
      bind:value={details}
      use:autofocus
      use:autosize
      onkeydown={onKeyDown}
    ></textarea>
    <div class="button-group-right">
      <button
        class="tiny-button cancel"
        onclick={() => editMode = false}
      >
        {i18n('cancel')}
      </button>
      <button
        class="tiny-button success"
        onclick={save}
      >
        {i18n('save')}
      </button>
    </div>
  </div>
{:else if restricted}
  <div class="item-details">
    {@render header()}
    <div class="text">
      <p>{@html userContent(details)}</p>
    </div>
  </div>
{:else}
  <button
    class="item-details"
    onclick={onEditButtonClick}
    title={I18n('edit')}
  >
    {@render header()}
    <div class="text">
      <p>{@html userContent(details)}</p>
    </div>
  </button>
{/if}

<style lang="scss">
  @import "general/scss/utils";
  .item-details{
    display: block;
    width: 100%;
    background-color: $off-white;
    padding: 0.5em 1em;
    margin: 0.4em 0 0.6em;
    line-height: 1.6rem;
    @include radius;
  }
  button .text{
    text-align: start;
    display: block;
    padding: 0;
    user-select: text;
  }
  .tiny-button{
    line-height: inherit;
  }
  .header{
    @include display-flex(row, center);
    height: 2rem;
  }
  .indicator{
    margin-inline-start: auto;
  }
</style>
